import React, { useState } from 'react';
import { DollarSign, BookOpen, GraduationCap, Calculator } from 'lucide-react';

const TuitionFees = ({ feesData }) => {
  const [activeTab, setActiveTab] = useState('undergraduate');

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {feesData?.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive breakdown of tuition fees and additional costs for international students
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-md">
            <button
              onClick={() => setActiveTab('undergraduate')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'undergraduate'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <GraduationCap className="w-5 h-5 inline mr-2" />
              Undergraduate
            </button>
            <button
              onClick={() => setActiveTab('postgraduate')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'postgraduate'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <BookOpen className="w-5 h-5 inline mr-2" />
              Postgraduate
            </button>
          </div>
        </div>

        {/* Tuition Fees Content */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Main Fee Structure */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {activeTab === 'undergraduate' ? feesData?.undergraduate?.title : feesData?.postgraduate?.title}
              </h3>
              
              <div className="space-y-4">
                {(activeTab === 'undergraduate' ? feesData?.undergraduate?.fees : feesData?.postgraduate?.fees)?.map((program, index) => (
                  <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center">
                      <DollarSign className="w-5 h-5 text-green-600 mr-3" />
                      <span className="font-medium text-gray-900">{program.program}</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600">{program.range}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Costs */}
          <div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Calculator className="w-6 h-6 mr-2 text-purple-600" />
                {feesData?.additionalCosts?.title}
              </h3>
              
              <div className="space-y-4">
                {feesData?.additionalCosts?.costs?.map((cost, index) => (
                  <div key={index} className="border-b border-gray-100 pb-3">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {cost.item}
                    </div>
                    <div className="text-blue-600 font-semibold">
                      {cost.cost}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Fee Calculator CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">
              Calculate Your Total Study Cost
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Get a personalized estimate of your total expenses including tuition, living costs, and other fees based on your chosen program and city.
            </p>
            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center">
                <Calculator className="w-5 h-5 mr-2" />
                Use Fee Calculator
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-200">
                Download Fee Guide
              </button>
            </div> */}
          </div>
        </div>

        {/* Important Note */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-yellow-800 font-bold text-sm">!</span>
              </div>
            </div>
            <div className="ml-3">
              <h4 className="text-lg font-medium text-yellow-800 mb-2">Important Note</h4>
              <p className="text-yellow-700">
                Tuition fees may vary based on the specific program, university, and academic year. The fees mentioned above are approximate ranges. 
                Please consult with our counselors for the most up-to-date and program-specific fee information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TuitionFees;